/*
    Created by Phat Hoang Pham
    phat@launchdeck.org
*/

import React, { useState, useImperativeHandle } from 'react';
import { Image, Text, View } from 'react-native-web';
import PropTypes from 'prop-types';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { googleLogout, useGoogleLogin } from '@react-oauth/google';
import { cloneDeep } from 'lodash';

// Custom Components
import { Button, InScreenModal, NavBar } from '../../components';

// Utils
import { showAppError, showAlert, storeData } from '../../utils/util_helpers';
import { authGoogleEmail, removeGoogleAccount } from '../../modules';

// Configs
import GLOBAL from '../../configs/config_global';

export function GoogleLoginModal(props, ref) {
    const { onGoogleDataUpdated } = props;

    // region Local State
    const [googleData, setGoogleData] = useState(null);
    const [visible, setVisible] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [currentCallback, setCurrentCallback] = useState(() => () => {});
    // endregion

    // region Expose Methods
    useImperativeHandle(
        ref,
        () => ({
            showModal: (callback = () => {}) => {
                setGoogleData(!!GLOBAL.DATA.USER['google_email'] ? { email: GLOBAL.DATA.USER['google_email'] } : null);
                setCurrentCallback(() => callback);
                setVisible(true);
            },
            hideModal: () => setVisible(false)
        }),
        []
    );
    // endregion

    // region Function - Handle Google Error
    const handleGoogleError = (error) => {
        if (error.toString().includes('"code": 401') || error.toString()?.toLowerCase().includes('sign out and sign back in to your google account')) {
            showAlert({
                text: 'Your Google login session has expired, tap confirm to sign out and sign in again with your Google account',
                cancelButton: { text: 'Cancel' },
                confirmButton: {
                    text: 'Confirm',
                    onPress: () => {
                        googleLogout();
                        setLoading(true);
                        setTimeout(() => {
                            goFetchLoginGoogle();
                        }, 500);
                    }
                }
            })
        }
        else showAppError(error);
    }
    // endregion

    // region Function - Go Fetch Google Login
    const scopeList = ['https://www.googleapis.com/auth/calendar.readonly', 'https://www.googleapis.com/auth/calendar.events.readonly'];
    const goFetchLoginGoogle = useGoogleLogin({
        flow: 'auth-code',
        scope: scopeList.join(' '),
        onSuccess: (codeResponse) => {
            console.log('[GOOGLE SIGN IN] TOKEN INFO', codeResponse);

            // Call server API
            authGoogleEmail(codeResponse.code)
                .then((authResult) => {
                    storeData('googleData', JSON.stringify(authResult)).then();
                    GLOBAL.DATA.USER = Object.assign(cloneDeep(GLOBAL.DATA.USER), authResult);
                    setVisible(false);
                    currentCallback();
                    onGoogleDataUpdated();
                })
                .catch((error) => handleGoogleError(error))
                .finally(() => setLoading(false));
        },
        onError: (errorResponse) => {
            showAppError(`${errorResponse.error} | ${errorResponse.error_description}`);
            setLoading(false);
        },
        onNonOAuthError: (nonOAuthError) => {
            setLoading(false);
            if (nonOAuthError.type !== 'popup_closed') {
                showAppError(nonOAuthError.type);
            }
        }
    });
    // endregion

    // region Function - Sign Out of Google
    const signOutGoogle = () => {
        setLoading(true);
        googleLogout();
        Promise.all([AsyncStorage.removeItem('googleData'), removeGoogleAccount()])
            .then(() => {
                console.log('[SUCCESS] GOOGLE SIGNED OUT');
                GLOBAL.DATA.USER = Object.assign(cloneDeep(GLOBAL.DATA.USER), { google_email: null });
                setVisible(false);
                currentCallback();
                onGoogleDataUpdated();
            })
            .catch((allError) => {
                console.log('[FAILED] GOOGLE SIGNED OUT', allError);
                showAppError(allError.message);
            })
            .finally(() => setLoading(false));
    }
    // endregion

    // region MAIN RENDER
    return(
        <InScreenModal visible={visible}>
            {/* Nav Bar */}
            <NavBar title={'Google Account'} leftButtonOnPress={() => setVisible(false)} />

            {/* Main View */}
            <View style={{ flex: 1, width: '100%', backgroundColor: 'white', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 64 }}>
                <Text style={{ fontWeight: '900', fontSize: 28, textAlign: 'center' }}>
                    {googleData ? 'You\'re signed in' : 'Google Account'}
                </Text>

                <Text style={{ marginTop: 16, fontSize: 18, color: GLOBAL.COLOR.TEXT_GRAY, textAlign: 'center', lineHeight: 26, marginBottom: 32 }}>
                    Signing in with a Google account will help you import events from your Google Calendar to Truckster either with Auto-Sync or manual imports
                </Text>

                {/* Current Signed In Email */}
                {
                    !!googleData &&
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 32 }}>
                        <Image
                            source={require('../../images/image_google_color.png')}
                            style={{ width: 24, height: 24 }}
                        />

                        <Text style={{ fontSize: 18, marginLeft: 12 }}>
                            Signed in as{' '}
                            <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.BASIC.UI_BLUE }}>
                                {googleData.email}
                            </Text>
                        </Text>
                    </View>
                }

                {/* Sign In Button */}
                <Button
                    text={googleData ? 'Sign out from Google' : 'Sign in with Google'}
                    style={{ backgroundColor: googleData ? GLOBAL.COLOR.BASIC.BROWN_RED : GLOBAL.COLOR.BASIC.UI_BLUE }}
                    useFeather={true}
                    image={googleData ? 'log-out' : require('../../images/iconSocialGoogle.png')}
                    imageSize={24}
                    onPress={() => {
                        if (googleData) {
                            signOutGoogle();
                        }
                        else {
                            googleLogout();
                            setLoading(true);
                            setTimeout(() => goFetchLoginGoogle(), 500);
                        }
                    }}
                    isLoading={isLoading}
                />
            </View>
        </InScreenModal>
    )
    // endregion
}

GoogleLoginModal = React.forwardRef(GoogleLoginModal);

// region Props
GoogleLoginModal.propTypes = {
    onGoogleDataUpdated: PropTypes.func
}

GoogleLoginModal.defaultProps = {
    onGoogleDataUpdated: () => {}
}
// endregion
